/**
 * CR306: Start URL section — entry point URL for crawl
 */

import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Globe, Save } from "lucide-react";
import type { Recipe } from "@/lib/recipes-studio/types";

interface RecipeStartUrlSectionProps {
  recipe: Recipe;
  saving: boolean;
  onSave: (updates: Partial<Recipe>) => void;
}

export function RecipeStartUrlSection({ recipe, saving, onSave }: RecipeStartUrlSectionProps) {
  const [url, setUrl] = useState(recipe.default_start_url ?? "");

  useEffect(() => {
    setUrl(recipe.default_start_url ?? "");
  }, [recipe.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasChanges = url !== (recipe.default_start_url ?? "");

  const handleSave = () => {
    onSave({ default_start_url: url.trim() || null });
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Globe className="h-4 w-4 text-muted-foreground" />
          Start URL
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Crawl Entry Point</Label>
          <Input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://example.com/deals"
          />
          <p className="text-[11px] text-muted-foreground">
            The URL Firecrawl will scrape. Supports full URLs with paths.
          </p>
        </div>

        {hasChanges && (
          <Button size="sm" onClick={handleSave} disabled={saving || !url.trim()}>
            <Save className="h-3.5 w-3.5 mr-1.5" />
            {saving ? "Saving…" : "Save"}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
